/**
 * Lightweight markdown-to-React-elements renderer.
 *
 * Handles: # headers (h1-h3), **bold**, *italic*, `code`, [links](url),
 * - unordered lists, \n\n paragraphs, ``` code blocks.
 *
 * Returns React elements (no dangerouslySetInnerHTML).
 */
import { createElement, type ReactNode } from 'react';

/** Parse inline markdown within a single line of text. */
function parseInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let remaining = text;
  let idx = 0;

  while (remaining.length > 0) {
    // Code span: `code`
    const codeMatch = remaining.match(/^`([^`]+)`/);
    if (codeMatch) {
      nodes.push(
        createElement(
          'code',
          {
            key: `${keyPrefix}-c${idx++}`,
            style: {
              backgroundColor: 'var(--sw-bg-surface)',
              padding: '1px 4px',
              borderRadius: '3px',
              fontSize: '0.9em',
              fontFamily: 'monospace',
            },
          },
          codeMatch[1],
        ),
      );
      remaining = remaining.slice(codeMatch[0].length);
      continue;
    }

    // Bold: **text**
    const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
    if (boldMatch) {
      nodes.push(
        createElement(
          'strong',
          { key: `${keyPrefix}-b${idx++}` },
          ...parseInline(boldMatch[1], `${keyPrefix}-bi${idx}`),
        ),
      );
      remaining = remaining.slice(boldMatch[0].length);
      continue;
    }

    // Italic: *text*
    const italicMatch = remaining.match(/^\*(.+?)\*/);
    if (italicMatch) {
      nodes.push(
        createElement(
          'em',
          { key: `${keyPrefix}-i${idx++}` },
          ...parseInline(italicMatch[1], `${keyPrefix}-ii${idx}`),
        ),
      );
      remaining = remaining.slice(italicMatch[0].length);
      continue;
    }

    // Link: [text](url)
    const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      nodes.push(
        createElement(
          'a',
          {
            key: `${keyPrefix}-a${idx++}`,
            href: linkMatch[2],
            target: '_blank',
            rel: 'noopener noreferrer',
            style: { color: 'var(--sw-accent-primary)', textDecoration: 'underline' },
          },
          linkMatch[1],
        ),
      );
      remaining = remaining.slice(linkMatch[0].length);
      continue;
    }

    // Plain text: consume up to the next special character
    const nextSpecial = remaining.search(/[`*\[]/);
    if (nextSpecial === -1) {
      nodes.push(remaining);
      break;
    } else if (nextSpecial === 0) {
      // Special char didn't match a pattern above — emit it literally
      nodes.push(remaining[0]);
      remaining = remaining.slice(1);
    } else {
      nodes.push(remaining.slice(0, nextSpecial));
      remaining = remaining.slice(nextSpecial);
    }
  }

  return nodes;
}

/**
 * Parse a markdown string into an array of React elements.
 */
export function renderMarkdown(markdown: string): ReactNode[] {
  const elements: ReactNode[] = [];
  const lines = markdown.split('\n');
  let blockKey = 0;

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block: ```
    if (line.trimStart().startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      elements.push(
        createElement(
          'pre',
          {
            key: `block-${blockKey++}`,
            style: {
              backgroundColor: 'var(--sw-bg-surface)',
              padding: '12px',
              borderRadius: 'var(--sw-border-radius)',
              overflowX: 'auto' as const,
              fontSize: '0.85em',
              fontFamily: 'monospace',
              margin: '8px 0',
              whiteSpace: 'pre-wrap' as const,
              wordBreak: 'break-word' as const,
            },
          },
          createElement('code', null, codeLines.join('\n')),
        ),
      );
      continue;
    }

    // Heading: # ## ###
    const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length as 1 | 2 | 3;
      const tag = `h${level}` as 'h1' | 'h2' | 'h3';
      const sizes = { 1: '1.4em', 2: '1.2em', 3: '1.05em' };
      elements.push(
        createElement(
          tag,
          {
            key: `block-${blockKey++}`,
            style: {
              fontSize: sizes[level],
              fontWeight: 600,
              margin: '16px 0 8px',
              color: 'var(--sw-text-primary)',
            },
          },
          ...parseInline(headingMatch[2], `h-${blockKey}`),
        ),
      );
      i++;
      continue;
    }

    // Unordered list item: - item or * item
    if (/^\s*[-*]\s+/.test(line)) {
      const listItems: ReactNode[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        const itemText = lines[i].replace(/^\s*[-*]\s+/, '');
        listItems.push(
          createElement(
            'li',
            {
              key: `li-${blockKey}-${listItems.length}`,
              style: { marginBottom: '4px' },
            },
            ...parseInline(itemText, `li-${blockKey}-${listItems.length}`),
          ),
        );
        i++;
      }
      elements.push(
        createElement(
          'ul',
          {
            key: `block-${blockKey++}`,
            style: { margin: '8px 0', paddingLeft: '20px' },
          },
          ...listItems,
        ),
      );
      continue;
    }

    // Empty line — skip
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Paragraph: collect consecutive non-empty, non-special lines
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].match(/^#{1,3}\s/) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !lines[i].trimStart().startsWith('```')
    ) {
      paraLines.push(lines[i]);
      i++;
    }

    if (paraLines.length > 0) {
      const text = paraLines.join(' ');
      elements.push(
        createElement(
          'p',
          {
            key: `block-${blockKey++}`,
            style: { margin: '8px 0', lineHeight: 1.6 },
          },
          ...parseInline(text, `p-${blockKey}`),
        ),
      );
    }
  }

  return elements;
}
